import * as React from 'react';
import styled from 'styled-components';
import {
  DefaultButton,
  DetailsList,
  DetailsRow,
  ISearchBox,
  PrimaryButton,
  SearchBox,
  Selection,
  SelectionMode,
  Spinner,
} from 'office-ui-fabric-react';

import Modal from 'views/components/UI/DefaultModal';
import RouteType from 'views/components/UI/RouteType';
import Rank from 'views/components/DrivingControl/RankIcon';
import { ViewSelection } from 'views/components/UI/ViewSelection';
import { IncidentTypeWrap } from 'views/pages/MapPage/components/DetailsPanel/IncidentTypeWrap';

import { IFilter } from 'api/filters';
import { theme } from 'theme';

const SearchBoxWrapper = styled.div`
  padding: 20px 25px;
  background: #ffffff;
`;

const Status = styled.div<{ isOnline: boolean }>`
  position: relative;
  display: inline-block;

  :before {
    content: '';
    display: block;
    position: absolute;
    top: 5px;
    left: -10px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.isOnline ? '#7FBA00' : '#CDCDCD')};
  }
`;

const FilterPicker = styled.div`
  width: 320px;
  max-height: 315px;
  min-height: 315px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;

  .ms-Check.is-checked {
    :before {
      background: ${theme.colors.lightBlue};
    }
  }

  .ms-DetailsRow {
    min-width: 100% !important;
  }
`;

const ErrorMessage = styled.div`
  font-size: 21px;
  font-weight: normal;
  color: #5f5d5b;
  margin: 58px 0 77px;
  text-align: center;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 70px;
  background: #ffffff;

  button:first-child {
    background: ${theme.colors.lightBlue};
  }
`;

interface IProps {
  title: string;
  onCloseModal: () => void;
  items: Array<any>;
  onFilter: (
    filteredArray: Array<{
      primaryField: string | number;
      key: string | number;
    }> | null,
    columnName: string
  ) => void;
  selectedFilters: { [key: string]: Array<string | number> | null };
  viewTitle: string;
  allFilters: IFilter | null;
  singleSelection?: boolean;
}

interface IState {
  searchFieldValue: string;
  items: Array<any>;
  filteredItems: Array<any>;
}

class FilterModal extends React.Component<IProps, IState> {
  private selection: Selection | null;
  private readonly searchBox: React.RefObject<ISearchBox>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      searchFieldValue: '',
      items: [],
      filteredItems: [],
    };

    this.searchBox = React.createRef<ISearchBox>();
    this.selection = null;
  }

  componentDidMount(): void {
    if (this.searchBox.current) {
      this.searchBox.current.focus();
    }

    this.selection = new Selection();
    this.setSelectedItems();

    new Promise((resolve) => {
      setTimeout(() => {
        resolve(this.reduceFilters(this.props.title));
      }, 0);
    }).then((result) => {
      this.setState(
        {
          filteredItems: result as Array<any>,
          items: result as Array<any>,
        },
      );
    });
  }

  setSelectedItems = () => {
    let { title, items } = this.props;
    let selectedFilters = JSON.parse(JSON.stringify(this.props.selectedFilters));
    this.selection!.setItems(items, true);

    selectedFilters[title] &&
      selectedFilters[title].forEach((filter: string | number) => {
        this.selection!.setKeySelected(filter.toString(), true, true);
      });
  };

  handleSearchFieldChange = (newValue: string) => {
    const filteredItems = newValue
      ? this.state.items.filter((item, index) => {
          return (
            item &&
            item.primaryField &&
            item.primaryField
              .toString()
              .toLocaleLowerCase()
              .includes(newValue.toLocaleLowerCase()) 
            || this.selection?.getSelectedIndices()?.includes(index)
          );
        })
      : this.state.items;
    this.setState({
      filteredItems,
      searchFieldValue: newValue,
    });
  };

  onRenderRows = (props: any) =>
    <DetailsRow
      data-selection-toggle={true}
      {...props}
    />;

  render() {
    const { onCloseModal, onFilter, viewTitle } = this.props;
    const { searchFieldValue, filteredItems } = this.state;

    return (
      <>
        <Modal handleClose={onCloseModal} title={viewTitle}>
          <SearchBoxWrapper>
            <SearchBox
              onChange={this.handleSearchFieldChange}
              value={searchFieldValue}
              componentRef={this.searchBox}
            />
          </SearchBoxWrapper>
          <FilterPicker>
            {this.selection ? (
              <>
                <ViewSelection selection={this.selection} items={filteredItems}>
                  <DetailsList
                    selectionMode={
                      this.props.singleSelection
                        ? SelectionMode.single
                        : undefined
                    }
                    items={filteredItems}
                    isHeaderVisible={false}
                    selection={this.selection}
                    columns={[
                      {
                        name: 'primaryField',
                        fieldName: 'primaryField',
                        key: 'primaryField',
                        minWidth: 0,
                      },
                    ]}
                    setKey={'set'}
                    selectionPreservedOnEmptyClick
                    onRenderRow={this.onRenderRows}
                    onRenderItemColumn={this.renderItemColumn}
                  />
                </ViewSelection>
                {!searchFieldValue || filteredItems.length ? null : (
                  <ErrorMessage>Ничего не найденно</ErrorMessage>
                )}
              </>
            ) : (
              <Spinner
                styles={{
                  root: {
                    position: 'relative',
                    minHeight: 300,
                    maxHeight: 300,
                  },
                  circle: {
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                    top: '50%',
                  },
                  label: {
                    position: 'absolute',
                    transform: 'translateY(-50%)',
                    top: '60%',
                  },
                }}
                size={3}
                label={'Загрузка'}
              />
            )}
          </FilterPicker>
          <StyledFooter>
            <PrimaryButton
              onClick={() => {
                this.selection &&
                  onFilter(
                    this.selection.getSelection() as Array<{
                      primaryField: string | number;
                      key: string | number;
                    }>,
                    this.props.title
                  );
                onCloseModal();
              }}
            >
              Выбрать
            </PrimaryButton>
            <DefaultButton
              onClick={() => {
                onFilter(null, this.props.title);
                onCloseModal();
              }}
            >
              Сбросить
            </DefaultButton>
          </StyledFooter>
        </Modal>
      </>
    );
  }

  renderItemColumn = (item: any) => {
    const fieldContent = item.primaryField as string;
    switch (this.props.title) {
      case 'routeType':
        return <RouteType type={item.key} />;
      case 'grades':
        return <Rank rank={item.key} />;
      case 'isOnline':
        return <Status isOnline={item.key === 'online'}>{fieldContent}</Status>;
      case 'lastIncident':
        return <IncidentTypeWrap type={item.key} />
      default:
        return <span>{fieldContent}</span>;
    }
  };

  private reduceFilters = (currentFilter: string) => {
    const shouldFiltering: { [key: string]: boolean } = {
      branch:
        !!this.props.selectedFilters.branch &&
        !!this.props.selectedFilters.branch.length,
      route:
        !!this.props.selectedFilters.route &&
        !!this.props.selectedFilters.route.length,
      depot:
        !!this.props.selectedFilters.depot &&
        !!this.props.selectedFilters.depot.length,
      routeType:
        !!this.props.selectedFilters.routeType &&
        !!this.props.selectedFilters.routeType.length,
      depotNumber:
        !!this.props.selectedFilters.depotNumber &&
        !!this.props.selectedFilters.depotNumber.length,
      gosNomer:
        !!this.props.selectedFilters.gosNomer &&
        !!this.props.selectedFilters.gosNomer.length,
      lastIncident:
        !!this.props.selectedFilters.lastIncident &&
        !!this.props.selectedFilters.lastIncident.length,
      model:
        !!this.props.selectedFilters.model &&
        !!this.props.selectedFilters.model.length
    };

    const rename = (name: string) => {
      switch (name) {
        case 'depot':
          return 'Depot_id';
        case 'route':
          return 'route_id';
        case 'lastIncident':
          return 'incident_type';
        default:
          return name;
      }
    };

    const filter = (name: string, vehicle: any) => {
      return this.props.selectedFilters[name]!.find(
        (item: any) => item === vehicle[rename(name)]
      );
    };

    const includingFilter = (vehicle: any) =>
      this.props.selectedFilters.route!.find((route) =>
        vehicle.routes.includes(route)
      );

    let filteredVehicles: any[] = [];

    const necessaryFilters = Object.keys(shouldFiltering).filter(
      (key) => shouldFiltering[key]
    );

    if (
      this.props.allFilters &&
      Object.values(shouldFiltering).some((column) => column)
    ) {
      const items = [...this.props.allFilters.vehicles];

      filteredVehicles = items.reduce((result: any[], vehicle: any) => {
        for (let column of necessaryFilters) {
          if (vehicle.routes) {
            if (column === 'route' && !includingFilter(vehicle)) {
              return result;
            } else if (column !== 'route' && !filter(column, vehicle)) {
              return result;
            }
          } else {
            if (!filter(column, vehicle)) {
              return result;
            }
          }
        }
        return [...result, vehicle];
      }, []);
    } else {
      return this.props.items;
    }

    if (!filteredVehicles.length) return [];

    switch (currentFilter) {
      case 'depotNumber':
      case 'gosNomer':
        return this.props.items.filter((v) =>
          filteredVehicles.find((vehicle) => vehicle.id === v.id)
        );
      case 'branch':
        return this.props.items.filter((branch) =>
          filteredVehicles.find((vehicle) => vehicle.branch === branch.id)
        );
      case 'depot':
        return this.props.items.filter((depot) =>
          filteredVehicles.find((vehicle) => vehicle.Depot_id === depot.id)
        );
      case 'route':
        return this.props.items.filter((route) =>
          filteredVehicles.find((vehicle) =>
            vehicle.routes
              ? vehicle.routes.includes(route.id)
              : vehicle.route_id === route.id
          )
        );
      case 'routeType':
      case 'grades':
        return this.props.items;
      case 'isOnline':
        return this.props.items.filter((item) => {
          const value = item.key === 'online';
          return filteredVehicles.find((v) => {
            return v.isOnline === value;
          });
        });
      case 'model':
        return this.props.items.filter(({model}) =>
          filteredVehicles.find((vehicle) => vehicle.model === model)
        );
      case 'lastIncident':
        return this.props.items.filter((item) =>
          filteredVehicles.find((vehicle) => vehicle.incident_type === item.key)
        );
    }

    filteredVehicles.forEach((vehicle) => {
      vehicle.primaryField = vehicle[this.props.title];
      vehicle.key = vehicle.id;
    });
    return filteredVehicles;
  };
}

export default FilterModal;
